import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { useAppSelector, useMedia, useUserTimezone } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { Column, Layout, MobileLayout } from '@/shared/ui/';

import { organizationModel } from '@/entities/organization';
import { reportsModel } from '@/entities/reports';

import { Footer } from '@/features/footer';
import { useDCCTVolumeSizeHint } from '@/features/MPR';
import { useReportCheck } from '@/features/reportCheck';

import { AboutLabelingModal } from '@/widgets/AboutLabelingModal';
import { GetFullAccessModal } from '@/widgets/GetFullAccessModal';
import { Header, MobileHeader } from '@/widgets/Header';

import { usePreviewDocument, usePrintSettings } from './hooks';
import styles from './PreviewReport.module.scss';
import { PreviewDocument } from './ui/PreviewDocument/PreviewDocument';
import { SettingsBar } from './ui/SettingsBar/SettingsBar';

type PreviewReportProps = unknown;

export const PreviewReport: FC<PreviewReportProps> = () => {
  const { patientID = '', reportID = '' } = useParams();
  const { isMobile } = useMedia();

  const organization = useAppSelector(
    organizationModel.selectors.selectCurrentOrganization,
  );

  const reportType = useAppSelector(
    reportsModel.selectors.selectCurrentReportType,
  );

  useReportCheck(reportID);
  useDCCTVolumeSizeHint({ patientID, reportID });

  const [organizationID, setOrganizationID] = useState(organization?.ID ?? '');

  const { printSettings, updatePrintSettings, isLoading, showLowProbability } =
    usePrintSettings(reportID);

  const documentData = usePreviewDocument({
    reportID,
    settings: printSettings,
  });

  const isPrintSettingsExist = printSettings !== undefined;

  const userTimezone = useUserTimezone();

  useEffect(() => {
    if (organization?.ID) {
      setOrganizationID(organization?.ID);
    }
  }, [organization]);

  useEffect(() => {
    if (reportType) {
      trackEvent({
        eventName: 'PreviewReport Page Viewed',
        properties: {
          patient_id: patientID,
          report_id: reportID,
          report_type: reportType,
        },
        isPageViewEvent: true,
      });
    }
  }, [reportType]);

  if (isMobile) {
    return (
      <>
        <MobileLayout>
          <MobileHeader />
          <MobileLayout.Main>
            {isPrintSettingsExist && (
              <>
                <SettingsBar
                  reportID={reportID}
                  settings={printSettings}
                  onChange={updatePrintSettings}
                  isLoading={isLoading}
                  patientID={patientID}
                />
                <PreviewDocument
                  documentData={documentData}
                  showLowProbability={showLowProbability}
                  settings={printSettings}
                  organizationID={organizationID}
                  userTimezone={userTimezone}
                />
              </>
            )}
          </MobileLayout.Main>
        </MobileLayout>

        {/* Modals */}
        <GetFullAccessModal />
        <AboutLabelingModal />
      </>
    );
  }

  return (
    <Layout>
      <Layout.Header className={styles.header}>
        <Header fullWidth />
      </Layout.Header>

      <Layout.Content
        className={cn(styles.content, styles.previewReportContent)}
        data-section="preview-report"
      >
        <Layout.Sider className={styles.sider}>
          {isPrintSettingsExist && (
            <SettingsBar
              reportID={reportID}
              settings={printSettings}
              onChange={updatePrintSettings}
              isLoading={isLoading}
              patientID={patientID}
            />
          )}
        </Layout.Sider>

        <Layout.Main>
          <Column className={styles.previewDocument}>
            {isPrintSettingsExist && (
              <PreviewDocument
                documentData={documentData}
                showLowProbability={showLowProbability}
                settings={printSettings}
                organizationID={organizationID}
                userTimezone={userTimezone}
              />
            )}
          </Column>
        </Layout.Main>
      </Layout.Content>

      <Layout.Footer>
        <Footer />
      </Layout.Footer>

      <GetFullAccessModal />

      <AboutLabelingModal />
    </Layout>
  );
};
